import React, { useEffect, useState } from 'react';
import Popup from 'reactjs-popup';
import { useHistory } from 'react-router';
import api from '../../api';
import { ToastContainer, toast } from 'react-toastify';
import { Button, Input } from '../../components/Common';

const TOAST_OPTIONS = {
    position: 'top-center',
    autoClose: 1500,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

const errorMessage = err => err?.response?.data?.error || err?.message || 'Please try again.';

// Deleting a gateway stops remote control for a live customer installation, so a mis-click
// must not be enough: the operator has to type "delete" plus the serial number (or the
// gateway name when no serial is known) before the delete button arms itself.
const DeleteGatewayModal = ({ deviceId, gatewayName, serial, open, setOpen, onDeleted }) => {
    const history = useHistory();
    // Prefer the serial: it is printed on the physical unit, so retyping it proves the
    // operator is looking at the right box, not just the right browser tab.
    const confirmPhrase = `delete ${serial || gatewayName || 'gateway'}`;

    const [typed, setTyped] = useState('');
    const [error, setError] = useState('');
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        setTyped('');
        setError('');
    }, [open]);

    const armed = typed.trim() === confirmPhrase && !deleting;

    const handleClose = () => {
        setOpen(false);
        setTyped('');
        setError('');
    };

    const doDelete = async () => {
        if (!armed) {
            return;
        }
        setError('');
        setDeleting(true);
        try {
            await api.delete_device(deviceId);
            // Reset before navigating: the modal can stay mounted briefly (or be reopened),
            // and a stuck deleting=true would keep the button disarmed forever.
            setDeleting(false);
            toast.success('Gateway deleted', TOAST_OPTIONS);
            setOpen(false);
            if (onDeleted) {
                // Opened from a list: the caller refreshes in place instead of navigating.
                onDeleted();
            } else {
                // Back to the fleet list: this page's gateway no longer exists, and the list
                // refetches on mount so the deleted unit disappears without a manual reload.
                history.push('/gateways');
            }
        } catch (err) {
            // Keep the modal open on failure: the operator keeps the context to retry or abort.
            setError(`Could not delete the gateway. ${errorMessage(err)}`);
            setDeleting(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <Popup open={open} onClose={handleClose} modal className="modal" contentStyle={{ maxWidth: 460, width: '90%', borderRadius: 15 }}>
                <div>
                    <button type="button" className="modal__close" aria-label="Close delete gateway dialog" onClick={handleClose}>
                        &#10005;
                    </button>
                    <h5 className="modal__header"> Delete gateway </h5>
                    <div className="modal__content" style={{ alignItems: 'stretch', textAlign: 'left', padding: '24px 28px 8px' }}>
                        <p style={{ margin: 0 }}>
                            This deletes the installation: remote control stops, the device is removed from its facility and from the fleet views. The
                            physical unit keeps running on its own and can only be brought back by onboarding it again by serial number.
                        </p>
                        <label htmlFor="deleteGatewayConfirm" style={{ fontWeight: 600, margin: '16px 0 4px' }}>
                            Type <span style={{ fontFamily: 'monospace' }}>{confirmPhrase}</span> to confirm
                        </label>
                        <Input
                            id="deleteGatewayConfirm"
                            type="text"
                            placeholder={confirmPhrase}
                            value={typed}
                            onChange={e => setTyped(e.target.value)}
                            fullWidth
                        />
                        {error !== '' && (
                            <p className="modal__error-message" style={{ marginTop: 8 }}>
                                {error}
                            </p>
                        )}
                    </div>

                    <div className="modal__actions">
                        <button
                            type="button"
                            className="button"
                            onClick={doDelete}
                            disabled={!armed}
                            style={{ backgroundColor: armed ? '#b91c1c' : '#e5a3a3', cursor: armed ? 'pointer' : 'not-allowed' }}
                        >
                            {deleting ? 'Deleting…' : 'Delete gateway'}
                        </button>
                        <Button text="Cancel" onClick={handleClose} className="modal__cancel" />
                    </div>
                </div>
            </Popup>
        </>
    );
};

export default DeleteGatewayModal;
